import axiosInstance from '../axiosInstance';
import { getManagerRoutePath } from '../managerRouteUtil';

/**
 * Service wrapper for the manager-side Employee Unavailability endpoints
 * (carer holidays / leave / sickness). Mirrors the backend's REST surface:
 *   GET    /unavailability             -> list
 *   POST   /unavailability             -> create
 *   DELETE /unavailability/:id         -> remove
 *
 * The dashboard never resolves carer-availability by date itself when it
 * can avoid it; the carer payload already embeds upcoming unavailability
 * windows. These helpers are used by the duty-roster Employee modal where
 * the manager directly manages the holiday list for one staff member.
 */
const unavailabilityService = {
  /**
   * List unavailability rows, optionally filtered by employee and / or
   * overlapping a date window.
   *
   * @param {object} [opts]
   * @param {number|string} [opts.employeeId]
   * @param {string} [opts.start] - YYYY-MM-DD
   * @param {string} [opts.end]   - YYYY-MM-DD
   */
  list: async ({ employeeId, start, end } = {}) => {
    try {
      const routePath = getManagerRoutePath();
      const params = new URLSearchParams();
      if (employeeId) params.set('employee_id', String(employeeId));
      if (start) params.set('start', start);
      if (end) params.set('end', end);
      const query = params.toString();
      const response = await axiosInstance.get(
        `/${routePath}/unavailability${query ? `?${query}` : ''}`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching unavailability:', error);
      throw error;
    }
  },

  /**
   * Record a new holiday / leave window for a carer.
   *
   * @param {object} payload
   * @param {number} payload.employee_id
   * @param {string} payload.start_date - YYYY-MM-DD inclusive
   * @param {string} payload.end_date   - YYYY-MM-DD inclusive
   * @param {string} [payload.reason]   - e.g. "Annual leave"
   * @param {string} [payload.notes]
   */
  create: async (payload) => {
    try {
      const routePath = getManagerRoutePath();
      const response = await axiosInstance.post(
        `/${routePath}/unavailability`,
        payload
      );
      return response.data;
    } catch (error) {
      console.error('Error creating unavailability:', error);
      throw error;
    }
  },

  remove: async (id) => {
    try {
      const routePath = getManagerRoutePath();
      const response = await axiosInstance.delete(
        `/${routePath}/unavailability/${id}`
      );
      return response.data;
    } catch (error) {
      console.error('Error removing unavailability:', error);
      throw error;
    }
  },
};

export default unavailabilityService;
